import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { CategoryWithProducts } from '../components/Shop/Categories/types'

interface VirtualizedCategoryHook {
  visibleCategories: CategoryWithProducts[]
  loadMore: () => void
  hasMore: boolean
  sentinelRef: React.RefObject<HTMLDivElement>
}

interface SentinelProps {
  hasMore: boolean
  sentinelRef: React.RefObject<HTMLDivElement>
}

export const useVirtualizedCategories = (
  categories: CategoryWithProducts[],
  initialCount: number,
  incrementCount: number,
  showAllCategories: boolean = false
): VirtualizedCategoryHook => {
  const [visibleCount, setVisibleCount] = useState(initialCount)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const visibleCategories = useMemo(
    () => (showAllCategories ? categories : categories.slice(0, visibleCount)),
    [categories, visibleCount, showAllCategories]
  )

  const hasMore = showAllCategories ? false : visibleCount < categories.length

  const loadMore = useCallback(() => {
    if (hasMore) {
      setVisibleCount((prev) =>
        Math.min(prev + incrementCount, categories.length)
      )
    }
  }, [hasMore, incrementCount, categories.length])

  // Intersection observer calls loadMore on intersection with the sentinel
  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore()
        }
      },
      {
        rootMargin: '0px 0px 30% 0px', // Load next batch of categories before user reaches the end - trigger when sentinel is 30% of screen height away from entering view
        threshold: 0
      }
    )

    observer.observe(sentinelRef.current)

    return () => observer.disconnect()
  }, [loadMore, hasMore])

  return {
    visibleCategories,
    loadMore,
    hasMore,
    sentinelRef
  }
}

export const InfiniteScrollSentinel = ({
  hasMore,
  sentinelRef
}: SentinelProps) => {
  if (!hasMore) return null

  return (
    <div
      ref={sentinelRef}
      style={{
        height: '10px',
        width: '100%',
        backgroundColor: 'transparent'
      }}
    />
  )
}
